import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import Modal from 'react-bootstrap/Modal';
import { Elements } from 'react-stripe-elements';
import { Lock, ShieldCheck, X, CreditCard } from 'lucide-react';
import FormSelect from 'components/common/FormSelect';
import LoadingAnimation from 'components/common/LoadingAnimation';
import { priceDecimalFormatter } from 'formatter/preset.formatter';
import { UPDATE_SAVED_CARD_IN_CATEGORING_ORDER } from 'redux/catering/actionTypes';
import InjectedCheckoutForm from './InjectedCheckoutForm';

const CARD_BRAND_BADGES = [
  { name: 'Visa', bg: '#1A1F71', label: 'VISA' },
  { name: 'Mastercard', bg: '#EB001B', label: 'MC' },
  { name: 'Amex', bg: '#2E77BC', label: 'AMEX' },
  { name: 'Discover', bg: '#FF6000', label: 'DISC' },
];

const StripeFormModal = ({
  show,
  onClose,
  orderHandler,
  loadingHandler,
  totalPrice,
  cateringOrder,
  clearCateringOrder,
  loader,
  setSaveCard,
  isEft,
}) => {
  const dispatch = useDispatch();
  const [active, setActive] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);

  useEffect(() => {
    const channel = new BroadcastChannel('card_channel');
    channel.onmessage = event => {
      const eventCard = event.data;
      dispatch({
        type: UPDATE_SAVED_CARD_IN_CATEGORING_ORDER,
        data: eventCard,
      });
    };
    return () => {
      channel.close();
    };
  }, []);
  const defaultSelectedCard = useMemo(
    () =>
      cateringOrder?.savedCards?.find(
        item =>
          item?.isDefault &&
          (item.paymentType === 'card' || item.paymentType == null),
      ),
    [cateringOrder?.savedCards],
  );
  const options = useMemo(
    () =>
      cateringOrder?.savedCards
        ?.filter(
          item =>
            item.paymentType === 'card' || item.paymentType !== 'acss_debit',
        )
        ?.map(item => ({
          label: item.name,
          value: item.paymentMethodId,
        })),
    [cateringOrder?.savedCards],
  );

  useEffect(() => {
    setSelectedCard(
      defaultSelectedCard
        ? {
            label: defaultSelectedCard?.name,
            value: defaultSelectedCard?.paymentMethodId,
          }
        : null,
    );
    if (defaultSelectedCard) {
      setActive(defaultSelectedCard?.paymentMethodId);
    } else if (cateringOrder && !cateringOrder?.savedCards?.length) {
      setActive('add');
    } else {
      setActive(null);
      setSelectedCard(null);
    }
  }, [cateringOrder, defaultSelectedCard, cateringOrder?.savedCards]);

  const handleSubmit = () => {
    if (active && cateringOrder.purchaseId !== 'add') {
      orderHandler(active);
    }
  };

  const isLoading = loader !== undefined ? loader : !cateringOrder;

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      dialogClassName="panda-pay-modal-dialog"
      contentClassName="!rounded-3xl !border-0 !overflow-hidden !p-0 shadow-2xl"
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute right-3 top-3 z-20 w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center backdrop-blur-xs transition-colors"
      >
        <X className="w-5 h-5" />
      </button>

      <div className="relative bg-gradient-to-br from-[#7B0D0D] via-[#991B1B] to-[#B91C1C] px-6 pt-7 pb-14 text-center overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-white/10 via-transparent to-transparent pointer-events-none" />
        <div className="relative w-16 h-16 mx-auto rounded-full bg-white p-1 shadow-lg ring-2 ring-white/30">
          <img
            src="/panda_logo.jpg"
            alt="Panda On Wheels"
            className="w-full h-full object-cover rounded-full"
            referrerPolicy="no-referrer"
          />
        </div>
        <h3 className="relative mt-3 text-white font-heading font-extrabold text-xl tracking-tight">
          Panda On Wheels
        </h3>
        <p className="relative mt-1 text-red-100 text-xs font-semibold uppercase tracking-wider flex items-center justify-center gap-1.5">
          <Lock className="w-3 h-3" />
          Secure Checkout
        </p>
      </div>

      <div className="relative -mt-9 px-6">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 px-5 py-4 text-center">
          <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">
            Total Amount Due
          </p>
          <p className="mt-1 text-3xl font-heading font-extrabold text-[#202124]">
            ${priceDecimalFormatter(totalPrice)}
          </p>
          <p className="mt-0.5 text-[11px] text-gray-400">
            Includes delivery fee &amp; applicable tax
          </p>
        </div>
      </div>

      <Modal.Body className="!px-6 !pt-5 !pb-6">
        <div className="flex items-center justify-center gap-2 mb-5">
          {CARD_BRAND_BADGES.map(brand => (
            <span
              key={brand.name}
              title={brand.name}
              style={{ backgroundColor: brand.bg }}
              className="inline-flex items-center justify-center h-6 px-2 rounded-md text-white text-[10px] font-extrabold tracking-wide shadow-xs"
            >
              {brand.label}
            </span>
          ))}
          <span className="inline-flex items-center gap-1 text-[11px] text-gray-400 ml-1">
            <ShieldCheck className="w-3.5 h-3.5 text-[#65A30D]" />
            Powered by Stripe
          </span>
        </div>

        {cateringOrder?.savedCards?.length ? (
          <div className="mb-4">
            <label
              htmlFor="numPeopleRange"
              className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5"
            >
              Choose Payment Method
            </label>
            <FormSelect
              name="numPeopleRange"
              placeholder="Pay with"
              value={selectedCard}
              options={[...options, { label: 'Add new card', value: 'add' }]}
              onChange={(_, value, card) => {
                setActive(value);
                setSelectedCard(card);
              }}
            />
          </div>
        ) : null}

        {isLoading ? <LoadingAnimation /> : null}

        {active === 'add' ? (
          <>
            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4">
              <p className="flex items-center gap-1.5 text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                <CreditCard className="w-3.5 h-3.5 text-[#E53935]" />
                Card Details
              </p>
              <Elements>
                <InjectedCheckoutForm
                  {...{
                    orderHandler,
                    loadingHandler,
                    totalPrice,
                    setSaveCard,
                    disabled: !cateringOrder,
                  }}
                />
              </Elements>
            </div>
            {!cateringOrder && (
              <p className="text-center text-xs text-gray-500 mt-3">
                Preparing your order before payment&hellip;
              </p>
            )}
          </>
        ) : (
          <>
            {defaultSelectedCard &&
            defaultSelectedCard?.brand &&
            defaultSelectedCard?.last4 ? (
              <div className="mb-4 rounded-2xl bg-gradient-to-br from-[#202124] to-[#3a3d42] text-white p-4 flex items-center justify-between shadow-md">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center">
                    <CreditCard className="w-4.5 h-4.5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-bold capitalize">
                      {defaultSelectedCard?.brand} &bull;&bull;&bull;&bull;{' '}
                      {defaultSelectedCard?.last4}
                    </p>
                    <p className="text-[11px] text-white/50">
                      Default payment method
                    </p>
                  </div>
                </div>
                <Link
                  target="_blank"
                  to="/payments"
                  className="text-[11px] font-semibold text-white/80 hover:text-white underline"
                >
                  Change
                </Link>
              </div>
            ) : null}
            <button
              type="button"
              disabled={!active}
              onClick={handleSubmit}
              className="w-full py-3.5 rounded-xl bg-[#E53935] hover:bg-[#d32f2f] disabled:opacity-40 disabled:pointer-events-none text-white font-heading font-bold text-base shadow-md shadow-red-500/20 active:scale-98 transition-all flex items-center justify-center gap-2"
            >
              <Lock className="w-4 h-4" />
              Pay ${priceDecimalFormatter(totalPrice)}
            </button>
          </>
        )}

        <p className="flex items-center justify-center gap-1.5 text-[11px] text-gray-400 mt-4">
          <ShieldCheck className="w-3.5 h-3.5 text-[#65A30D] flex-shrink-0" />
          Your payment is encrypted end-to-end. Panda On Wheels never stores
          your card details.
        </p>
      </Modal.Body>
    </Modal>
  );
};

StripeFormModal.propTypes = {
  show: PropTypes.bool,
  isEft: PropTypes.bool,
  onClose: PropTypes.func,
  orderHandler: PropTypes.func,
  loadingHandler: PropTypes.func,
  setSaveCard: PropTypes.func,
  totalPrice: PropTypes.number.isRequired,
  clearCateringOrder: PropTypes.func.isRequired,
  cateringOrder: PropTypes.shape({
    savedCards: PropTypes.arrayOf(
      PropTypes.objectOf({
        name: PropTypes.string,
        payment_method_id: PropTypes.string,
      }),
    ),
    clientSecret: PropTypes.string,
    purchaseId: PropTypes.number.isRequired,
  }).isRequired,
};

StripeFormModal.defaultProps = {
  show: true,
  isEft: false,
  onClose: () => {},
  orderHandler: () => {},
  loadingHandler: () => {},
  setSaveCard: () => {},
};

export default StripeFormModal;
